import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/Reveal";
import { ConsequentialGame } from "@/components/ConsequentialGame";
import { BecomingRelease } from "@/components/BecomingRelease";
import { VowLetter } from "@/components/VowLetter";
import { getArtwork, submitInquiry } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const INQUIRY_TYPES = [
  { value: "original", label: "Original purchase" },
  { value: "print", label: "Print" },
  { value: "custom-size", label: "Custom size" },
  { value: "custom-order", label: "Custom order / commission" },
  { value: "general", label: "General question" },
];

export default function ArtworkDetailPage() {
  const { slug } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", inquiry_type: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useSEO(
    artwork ? `${artwork.title} · The House of Ego` : "Artwork · The House of Ego",
    artwork
      ? `${artwork.title} by Seyoun Taylor, ${artwork.series} series. Inquire about originals, prints, and custom sizes.`
      : "Artwork by Seyoun Taylor."
  );

  useEffect(() => {
    getArtwork(slug)
      .then(setArtwork)
      .catch(() => setNotFound(true));
  }, [slug]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.inquiry_type) {
      toast.error("Please choose an inquiry type.");
      return;
    }
    setSending(true);
    try {
      await submitInquiry({ ...form, artwork_title: artwork.title });
      setSent(true);
      toast.success("Inquiry sent. Seyoun will reply personally.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <main id="main-content" className="mx-auto max-w-7xl px-6 md:px-10 pt-52 pb-32 text-center" data-testid="artwork-not-found">
        <h1 className="font-serif text-4xl text-ink">This work has moved.</h1>
        <Link to="/gallery" data-testid="not-found-back-link" className="mt-6 inline-block text-royal link-underline">
          Return to the gallery
        </Link>
      </main>
    );
  }

  if (!artwork) {
    return (
      <main id="main-content" className="mx-auto max-w-7xl px-6 md:px-10 pt-52 pb-32" data-testid="artwork-loading">
        <p className="text-neutral-400 text-sm">Unveiling the work…</p>
      </main>
    );
  }

  return (
    <main id="main-content" data-testid="artwork-detail-page">
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-32 md:pt-40 pb-24">
        <Link
          to="/gallery"
          data-testid="back-to-gallery-link"
          className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-royal transition-colors"
        >
          <ArrowLeft size={15} /> Back to the gallery
        </Link>

        <div className="mt-10 grid gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="bg-stone2 overflow-hidden">
              <img
                src={artwork.image_url}
                alt={`${artwork.title}, artwork by Seyoun Taylor`}
                data-testid="artwork-image"
                className="w-full object-cover"
              />
            </div>
          </Reveal>

          <div>
            <Reveal delay={0.1}>
              {artwork.series && <p className="eyebrow">{artwork.series}</p>}
              <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight text-ink" data-testid="artwork-title">
                {artwork.title}
              </h1>
              <dl className="mt-8 space-y-3 text-sm">
                {artwork.size && (
                  <div className="flex gap-6 border-b border-neutral-200 pb-3">
                    <dt className="w-28 text-neutral-500 uppercase tracking-[0.15em] text-xs pt-0.5">Size</dt>
                    <dd className="text-ink">{artwork.size}</dd>
                  </div>
                )}
                <div className="flex gap-6 border-b border-neutral-200 pb-3">
                  <dt className="w-28 text-neutral-500 uppercase tracking-[0.15em] text-xs pt-0.5">Medium</dt>
                  <dd className="text-ink">{artwork.medium}</dd>
                </div>
                <div className="flex gap-6 border-b border-neutral-200 pb-3">
                  <dt className="w-28 text-neutral-500 uppercase tracking-[0.15em] text-xs pt-0.5">Price</dt>
                  <dd className="text-ink">{artwork.price}</dd>
                </div>
              </dl>
              <div className="mt-6 space-y-4" data-testid="artwork-description">
                {artwork.description.split("\n\n").map((block, i) =>
                  block.startsWith("## ") ? (
                    <h3 key={i} className="pt-4 font-serif text-2xl text-ink">
                      {block.replace("## ", "")}
                    </h3>
                  ) : (
                    <p key={i} className="text-neutral-600 leading-relaxed">
                      {block}
                    </p>
                  )
                )}
              </div>
              {(artwork.images || []).filter((src) => src !== artwork.image_url).length > 0 && (
                <div className="mt-8 grid grid-cols-2 gap-4" data-testid="artwork-extra-images">
                  {(artwork.images || [])
                    .filter((src) => src !== artwork.image_url)
                    .map((src, i) => (
                      <img
                        key={src}
                        src={src}
                        alt={`${artwork.title}, exhibition material ${i + 1}`}
                        loading="lazy"
                        className="w-full border border-neutral-200 object-cover"
                      />
                    ))}
                </div>
              )}
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-12 bg-white border border-neutral-200 p-8 md:p-10" data-testid="inquiry-panel">
                {sent ? (
                  <div className="text-center py-8" data-testid="inquiry-success">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-royal-soft text-royal">
                      <Check size={22} />
                    </span>
                    <h2 className="mt-5 font-serif text-3xl text-ink">Inquiry received.</h2>
                    <p className="mt-3 text-sm text-neutral-600 max-w-sm mx-auto">
                      Thank you. Your note about “{artwork.title}” has been sent. Seyoun replies to
                      every inquiry personally.
                    </p>
                    <Link
                      to="/gallery"
                      data-testid="inquiry-success-back"
                      className="mt-6 inline-block text-sm text-royal link-underline"
                    >
                      Continue browsing
                    </Link>
                  </div>
                ) : (
                  <>
                    <h2 className="font-serif text-3xl text-ink">Inquire about this work</h2>
                    <p className="mt-2 text-sm text-neutral-500">
                      Originals, prints, custom sizes, and commissions.
                    </p>
                    <form onSubmit={onSubmit} className="mt-8 space-y-7" data-testid="inquiry-form">
                      <div className="grid sm:grid-cols-2 gap-7">
                        <div>
                          <Label htmlFor="inq-name" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Name
                          </Label>
                          <Input
                            id="inq-name"
                            data-testid="inquiry-name-input"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="field"
                            placeholder="Your name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="inq-email" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Email
                          </Label>
                          <Input
                            id="inq-email"
                            type="email"
                            data-testid="inquiry-email-input"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="field"
                            placeholder="you@example.com"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                          Inquiry type
                        </Label>
                        <Select
                          value={form.inquiry_type}
                          onValueChange={(v) => setForm({ ...form, inquiry_type: v })}
                        >
                          <SelectTrigger
                            data-testid="inquiry-type-select"
                            className="field mt-1 flex justify-between"
                          >
                            <SelectValue placeholder="Choose one" />
                          </SelectTrigger>
                          <SelectContent>
                            {INQUIRY_TYPES.map((t) => (
                              <SelectItem key={t.value} value={t.value} data-testid={`inquiry-type-${t.value}`}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="inq-message" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                          Message
                        </Label>
                        <Textarea
                          id="inq-message"
                          data-testid="inquiry-message-input"
                          required
                          rows={4}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          className="field resize-none"
                          placeholder={`Tell Seyoun what you love about “${artwork.title}”…`}
                        />
                      </div>
                      <button
                        type="submit"
                        data-testid="inquiry-submit-button"
                        disabled={sending}
                        className="w-full bg-royal text-white py-4 text-sm tracking-wide hover:bg-royal-deep transition-colors disabled:opacity-60"
                      >
                        {sending ? "Sending…" : "Send inquiry"}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {artwork.slug === "the-consequential-game-installation" && (
        <Reveal>
          <div className="mx-auto max-w-7xl px-6 md:px-10 pb-24" data-testid="installation-game">
            <ConsequentialGame />
          </div>
        </Reveal>
      )}

      {artwork.slug === "becoming" && (
        <Reveal>
          <div className="mx-auto max-w-7xl px-6 md:px-10 pb-24" data-testid="becoming-interactive">
            <BecomingRelease />
          </div>
        </Reveal>
      )}

      {artwork.slug === "vow-of-transformation" && (
        <Reveal>
          <div className="mx-auto max-w-7xl px-6 md:px-10 pb-24" data-testid="vow-interactive">
            <VowLetter />
          </div>
        </Reveal>
      )}
    </main>
  );
}
